import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Search, ScrollText, Loader2, ShieldAlert } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { auditApi, usersApi, type AuditListParams } from "@/lib/api"
import { formatDateTime, timeAgo } from "@shared/utils/date"
import { useAuth } from "@/hooks/useAuth"

const ACTION_CATEGORY: Record<string, "info" | "warning" | "success" | "destructive" | "muted" | "default"> = {
  login_ok: "success",
  login_fail: "warning",
  login_rate_limited: "destructive",
  logout: "muted",
  totp_enrol_completed: "success",
  totp_enrol_fail: "warning",
  contact_created: "info",
  contact_updated: "muted",
  contact_deleted: "destructive",
  deal_created: "success",
  deal_updated: "muted",
  deal_stage_changed: "info",
  deal_deleted: "destructive",
  task_created: "muted",
  task_updated: "muted",
  task_deleted: "destructive",
  document_uploaded: "info",
  document_downloaded: "muted",
  document_deleted: "destructive",
  integration_connected: "success",
  integration_disconnected: "warning",
  template_created: "info",
  template_updated: "muted",
  template_deleted: "destructive",
  note_added: "info",
  communication_added: "info",
  communication_deleted: "destructive",
  aircall_call_logged: "success",
  aircall_call_unmatched: "warning",
}

function categoryFor(action: string): "info" | "warning" | "success" | "destructive" | "muted" | "default" {
  return ACTION_CATEGORY[action] ?? "muted"
}

function formatMetadata(metaJson: string | null): string | null {
  if (!metaJson) return null
  try {
    const obj = JSON.parse(metaJson) as Record<string, unknown>
    return Object.entries(obj)
      .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
      .join(" · ")
  } catch {
    return metaJson.slice(0, 200)
  }
}

export default function AuditLogPage() {
  const { user } = useAuth()

  if (user && !["admin", "compliance"].includes(user.role)) {
    return (
      <AppShell title="Audit Log">
        <Card className="p-12 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-amber-100 text-amber-700">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">Restricted</p>
          <p className="text-muted-foreground mt-1 text-xs">
            Only admin and compliance roles can view the audit log.
          </p>
        </Card>
      </AppShell>
    )
  }

  const [searchInput, setSearchInput] = useState("")
  const [params, setParams] = useState<AuditListParams>({ page: 1, pageSize: 100 })

  const list = useQuery({
    queryKey: ["audit", params],
    queryFn: () => auditApi.list(params),
  })
  const actions = useQuery({
    queryKey: ["audit-actions"],
    queryFn: () => auditApi.actions(),
  })
  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setParams((p) => ({ ...p, q: searchInput.trim() || undefined, page: 1 }))
  }

  return (
    <AppShell
      title="Audit Log"
      subtitle="Every security-sensitive action, contact/deal change, and integration event"
    >
      <div className="space-y-4">
        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearch} className="relative min-w-64 flex-1">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search action, entity ID, metadata, IP…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </form>
            <select
              className="border-input h-10 rounded-md border bg-background px-3 text-sm"
              value={params.action ?? ""}
              onChange={(e) =>
                setParams((p) => ({ ...p, action: e.target.value || undefined, page: 1 }))
              }
            >
              <option value="">All actions</option>
              {actions.data?.actions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <select
              className="border-input h-10 rounded-md border bg-background px-3 text-sm"
              value={params.userId ?? ""}
              onChange={(e) =>
                setParams((p) => ({ ...p, userId: e.target.value || undefined, page: 1 }))
              }
            >
              <option value="">All users</option>
              {brokers.data?.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </select>
            {(params.q || params.action || params.userId) && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSearchInput("")
                  setParams({ page: 1, pageSize: 100 })
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </Card>

        {/* Results */}
        {list.isLoading ? (
          <Card className="grid place-items-center p-16">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </Card>
        ) : !list.data?.items.length ? (
          <Card className="p-12 text-center">
            <div className="bg-rc-blue-50 text-rc-blue-600 mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full">
              <ScrollText className="h-6 w-6" />
            </div>
            <p className="text-sm font-medium text-foreground">No audit entries match</p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-rc-grey-50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Time</th>
                  <th className="px-4 py-2 text-left font-medium">User</th>
                  <th className="px-4 py-2 text-left font-medium">Action</th>
                  <th className="px-4 py-2 text-left font-medium">Entity</th>
                  <th className="px-4 py-2 text-left font-medium">Details</th>
                  <th className="px-4 py-2 text-left font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-0 align-top">
                    <td className="px-4 py-2 text-xs text-muted-foreground" title={formatDateTime(row.createdAt)}>
                      {timeAgo(row.createdAt)}
                    </td>
                    <td className="px-4 py-2 text-xs text-foreground">
                      {row.userName ?? row.userEmail ?? <span className="text-muted-foreground">system</span>}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={categoryFor(row.action)} className="text-[10px]">
                        {row.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {row.entityType && (
                        <>
                          <span className="text-foreground">{row.entityType}</span>
                          {row.entityId && (
                            <span className="text-muted-foreground font-mono">
                              {" "}
                              · {row.entityId.slice(0, 12)}…
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {formatMetadata(row.metadata)}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground font-mono">
                      {row.ipAddress}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Pagination */}
        {list.data && list.data.total > (params.pageSize ?? 100) && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {(list.data.page - 1) * (list.data.pageSize ?? 100) + 1}–
              {Math.min(list.data.page * (list.data.pageSize ?? 100), list.data.total)} of{" "}
              {list.data.total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={list.data.page <= 1}
                onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={list.data.page * (list.data.pageSize ?? 100) >= list.data.total}
                onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
